import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Moon, Flower2, Gem, Droplets, Clock, ListChecks, Play, Pause, Volume2, Flame, CheckCircle, Heart, BookOpen } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const Rituals = () => {
  const { token } = useAuth();
  const [rituals, setRituals] = useState([]);
  const [meditations, setMeditations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moonPhase, setMoonPhase] = useState("");
  const [cyclePhase, setCyclePhase] = useState("");
  const [playingAudio, setPlayingAudio] = useState(null);
  const [meditationStartTimes, setMeditationStartTimes] = useState({});
  const [completedToday, setCompletedToday] = useState(new Set());
  const [streakData, setStreakData] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [ritualHistory, setRitualHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchData();
    
    // Cleanup audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const [moonRes, cycleRes] = await Promise.all([
        axios.get(`${API}/moon/current`),
        axios.get(`${API}/cycle/current`, { headers, withCredentials: true })
      ]);
      
      setMoonPhase(moonRes.data.phase_name);
      const currentCyclePhase = cycleRes.data.phase && cycleRes.data.phase !== "unknown" ? cycleRes.data.phase : "";
      setCyclePhase(currentCyclePhase);
      
      // Fetch rituals, meditations, and streak in parallel
      const requests = [
        axios.get(
          `${API}/rituals?moon_phase=${encodeURIComponent(moonRes.data.phase_name)}${currentCyclePhase ? `&cycle_phase=${currentCyclePhase}` : ""}`
        ),
        axios.get(
          `${API}/meditations?moon_phase=${encodeURIComponent(moonRes.data.phase_name)}${currentCyclePhase ? `&cycle_phase=${currentCyclePhase}` : ""}`
        )
      ];
      
      if (token) {
        requests.push(axios.get(`${API}/meditations/streak`, { headers, withCredentials: true }));
        requests.push(axios.get(`${API}/rituals/favorites`, { headers, withCredentials: true }).catch(() => null));
      }
      
      const [ritualsRes, meditationsRes, streakRes, favoritesRes] = await Promise.all(requests);
      
      setRituals(ritualsRes.data.rituals || []);
      setMeditations(meditationsRes.data.meditations || []);
      
      if (streakRes?.data) {
        setStreakData(streakRes.data);
      }
      if (favoritesRes?.data?.favorites) {
        setFavorites(new Set(favoritesRes.data.favorites.map(f => f.ritual_name)));
      }
    } catch (error) {
      // rituals degrade gracefully
    } finally {
      setLoading(false);
    }
  };

  const toggleAudio = (meditation) => {
    if (playingAudio === meditation.id) {
      // Pause current audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      // Complete the meditation
      if (token && meditationStartTimes[meditation.id]) {
        completeMeditation(meditation);
      }
      setPlayingAudio(null);
    } else {
      // Stop previous audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
        // Complete previous meditation if it was playing
        if (playingAudio && token && meditationStartTimes[playingAudio]) {
          const prevMeditation = meditations.find(m => m.id === playingAudio);
          if (prevMeditation) completeMeditation(prevMeditation);
        }
      }
      // Play new audio
      const audio = new Audio(meditation.audio_url);
      audio.play();
      audio.onended = () => {
        setPlayingAudio(null);
        audioRef.current = null;
        // Auto-complete when meditation ends
        if (token) {
          completeMeditation(meditation);
        }
      };
      audioRef.current = audio;
      setPlayingAudio(meditation.id);
      setMeditationStartTimes(prev => ({ ...prev, [meditation.id]: Date.now() }));
    }
  };

  const completeMeditation = async (meditation) => {
    if (!token) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const startTime = meditationStartTimes[meditation.id] || Date.now();
      const duration = Math.floor((Date.now() - startTime) / 1000);
      
      const res = await axios.post(`${API}/meditations/complete`, {
        meditation_id: meditation.id,
        duration_seconds: Math.max(duration, 30), // Minimum 30 seconds
        moon_phase: moonPhase,
        cycle_phase: cyclePhase
      }, { headers, withCredentials: true });
      
      // Update streak data
      if (res.data) {
        setStreakData(prev => ({
          ...prev,
          current_streak: res.data.current_streak,
          longest_streak: res.data.longest_streak,
          total_sessions: res.data.total_sessions,
          meditated_today: true
        }));
        setCompletedToday(prev => new Set([...prev, meditation.id]));
      }
      
      // Clear start time
      setMeditationStartTimes(prev => {
        const newTimes = { ...prev };
        delete newTimes[meditation.id];
        return newTimes;
      });
    } catch (error) {
      // rituals degrade gracefully
    }
  };

  const toggleFavorite = async (ritualName, ritualType) => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/rituals/favorite`, { ritual_name: ritualName, ritual_type: ritualType }, { headers, withCredentials: true });
      if (res.data.action === "favorited") {
        setFavorites(prev => new Set([...prev, ritualName]));
        toast.success("Added to favorites");
      } else {
        setFavorites(prev => { const n = new Set(prev); n.delete(ritualName); return n; });
        toast.success("Removed from favorites");
      }
    } catch (error) {
      // rituals degrade gracefully
    }
  };

  const completeRitual = async (ritualName, ritualType) => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/rituals/complete`, { ritual_name: ritualName, ritual_type: ritualType, moon_phase: moonPhase, cycle_phase: cyclePhase }, { headers, withCredentials: true });
      toast.success("Ritual completed! Well done.");
    } catch (error) {
      // rituals degrade gracefully
    }
  };

  const fetchHistory = async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/rituals/history`, { headers, withCredentials: true });
      setRitualHistory(res.data.history || []);
      setShowHistory(true);
    } catch (error) {
      // rituals degrade gracefully
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rituals-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-moon-gold" />
          Sacred Rituals
        </h1>
        <p className="text-mystic-200/70">Ceremonies for your current moon and cycle phases</p>
        <div className="flex gap-4 mt-3 items-center">
          {moonPhase && (
            <span className="text-sm bg-mystic-700/50 text-moon-gold px-3 py-1 rounded-full">
              <Moon className="w-3 h-3 inline mr-1" /> {moonPhase}
            </span>
          )}
          {cyclePhase && (
            <span className="text-sm bg-mystic-700/50 text-mystic-300 px-3 py-1 rounded-full capitalize">
              {cyclePhase} Phase
            </span>
          )}
          {token && (
            <Button variant="outline" size="sm" className="ml-auto border-mystic-600 text-mystic-200 hover:text-white" onClick={fetchHistory} data-testid="ritual-history-btn">
              <BookOpen className="w-4 h-4 mr-1" /> History
            </Button>
          )}
        </div>
      </motion.div>

      {showHistory && ritualHistory.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="glass border-mystic-600/30" data-testid="ritual-history-panel">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-mystic-300" /> Ritual History
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-mystic-200" onClick={() => setShowHistory(false)}>Close</Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {ritualHistory.map((entry, i) => (
                  <div key={`k-${i}`} className="flex items-center justify-between glass-light rounded-lg p-3">
                    <div>
                      <p className="text-white text-sm font-medium">{entry.ritual_name}</p>
                      <p className="text-mystic-200/60 text-xs">{entry.moon_phase && `${entry.moon_phase} `}{entry.cycle_phase && `- ${entry.cycle_phase}`}</p>
                    </div>
                    <span className="text-mystic-200/50 text-xs">{new Date(entry.completed_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {rituals.length === 0 ? (
        <Card className="glass">
          <CardContent className="py-12 text-center">
            <Sparkles className="w-12 h-12 text-mystic-400 mx-auto mb-4" />
            <p className="text-mystic-200/70">No specific rituals for the current phases.</p>
            <p className="text-mystic-300 text-sm mt-2">Log your cycle to get personalized recommendations.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {rituals.map((ritual, index) => (
            <motion.div
              key={ritual.name || `ritual-${index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`glass ${ritual.type === 'moon' ? 'border-moon-gold/20' : 'border-mystic-300/20'}`}>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                      {ritual.type === 'moon' ? (
                        <Moon className="w-5 h-5 text-moon-gold" />
                      ) : (
                        <Sparkles className="w-5 h-5 text-mystic-300" />
                      )}
                      {ritual.name}
                    </CardTitle>
                    <span className={`text-xs px-3 py-1 rounded-full ${
                      ritual.type === 'moon' ? 'bg-moon-gold/20 text-moon-gold' : 'bg-mystic-300/20 text-mystic-300'
                    }`}>
                      {ritual.type === 'moon' ? 'Moon Ritual' : 'Cycle Ritual'}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" data-testid={`favorite-ritual-${index}`} className={`${favorites.has(ritual.name) ? 'text-red-400' : 'text-mystic-200/50'} hover:text-red-400`} onClick={() => toggleFavorite(ritual.name, ritual.type)}>
                        <Heart className={`w-4 h-4 ${favorites.has(ritual.name) ? 'fill-current' : ''}`} />
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`complete-ritual-${index}`} className="text-green-400/70 hover:text-green-400" onClick={() => completeRitual(ritual.name, ritual.type)}>
                        <CheckCircle className="w-4 h-4 mr-1" /> Done
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Duration & Best Time */}
                  <div className="flex flex-wrap gap-4">
                    {ritual.duration && (
                      <div className="flex items-center gap-2 text-mystic-200/70">
                        <Clock className="w-4 h-4" />
                        <span>{ritual.duration}</span>
                      </div>
                    )}
                    {ritual.best_time && (
                      <div className="flex items-center gap-2 text-mystic-200/70">
                        <span>Best time: {ritual.best_time}</span>
                      </div>
                    )}
                    {ritual.focus && (
                      <div className="flex items-center gap-2 text-mystic-300">
                        <span>Focus: {ritual.focus}</span>
                      </div>
                    )}
                  </div>

                  {/* Sacred Items */}
                  {ritual.items && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {ritual.items.herbs && (
                        <div className="glass-light rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Flower2 className="w-4 h-4 text-green-400" />
                            <span className="text-white font-medium">Herbs</span>
                          </div>
                          <ul className="space-y-1">
                            {ritual.items.herbs.map((herb, i) => (
                              <li key={`k-${i}`} className="text-mystic-200/70 text-sm">• {herb}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {ritual.items.oils && (
                        <div className="glass-light rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Droplets className="w-4 h-4 text-amber-400" />
                            <span className="text-white font-medium">Oils</span>
                          </div>
                          <ul className="space-y-1">
                            {ritual.items.oils.map((oil, i) => (
                              <li key={`k-${i}`} className="text-mystic-200/70 text-sm">• {oil}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {ritual.items.crystals && (
                        <div className="glass-light rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Gem className="w-4 h-4 text-mystic-300" />
                            <span className="text-white font-medium">Crystals</span>
                          </div>
                          <ul className="space-y-1">
                            {ritual.items.crystals.map((crystal, i) => (
                              <li key={`k-${i}`} className="text-mystic-200/70 text-sm">• {crystal}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Steps or Practices */}
                  {(ritual.steps || ritual.practices) && (
                    <div className="glass-light rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <ListChecks className="w-4 h-4 text-mystic-300" />
                        <span className="text-white font-medium">
                          {ritual.steps ? 'Ritual Steps' : 'Practices'}
                        </span>
                      </div>
                      <ol className="space-y-2">
                        {(ritual.steps || ritual.practices).map((step, i) => (
                          <li key={`k-${i}`} className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-mystic-700/50 flex items-center justify-center text-xs text-mystic-300">
                              {i + 1}
                            </span>
                            <span className="text-mystic-200/80 text-sm">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Audio Meditations Section */}
      {meditations.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="glass bg-gradient-to-br from-indigo-500/10 to-purple-500/10">
            <CardHeader>
              <CardTitle className="text-xl text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Volume2 className="w-5 h-5 text-indigo-400" />
                  Guided Meditations
                </span>
                {token && streakData && (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="flex items-center gap-1 text-orange-400">
                      <Flame className="w-4 h-4" />
                      {streakData.current_streak} day streak
                    </span>
                    {streakData.meditated_today && (
                      <span className="flex items-center gap-1 text-green-400">
                        <CheckCircle className="w-4 h-4" />
                        Done today
                      </span>
                    )}
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {meditations.map((meditation, idx) => (
                  <motion.div
                    key={meditation.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`glass-light rounded-xl p-4 ${completedToday.has(meditation.id) ? 'ring-1 ring-green-500/30' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        meditation.category === 'moon' ? 'bg-moon-gold/20 text-moon-gold' :
                        meditation.category === 'cycle' ? 'bg-pink-500/20 text-pink-300' :
                        meditation.category === 'healing' ? 'bg-green-500/20 text-green-300' :
                        'bg-purple-500/20 text-purple-300'
                      }`}>
                        {meditation.phase}
                      </span>
                      <div className="flex items-center gap-2">
                        {completedToday.has(meditation.id) && (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        )}
                        <span className="text-mystic-400 text-xs">{meditation.duration}</span>
                      </div>
                    </div>
                    <h4 className="text-white font-medium mb-1">{meditation.title}</h4>
                    <p className="text-mystic-200/60 text-sm mb-3">{meditation.description}</p>
                    <Button
                      onClick={() => toggleAudio(meditation)}
                      variant="outline"
                      size="sm"
                      className={`w-full ${
                        playingAudio === meditation.id 
                          ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300' 
                          : completedToday.has(meditation.id)
                            ? 'border-green-500/50 text-green-300 hover:bg-green-500/20'
                            : 'border-mystic-500/50 text-mystic-300 hover:bg-mystic-500/20'
                      }`}
                      data-testid={`meditation-play-${meditation.id}`}
                    >
                      {playingAudio === meditation.id ? (
                        <>
                          <Pause className="w-4 h-4 mr-2" />
                          Pause
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          {completedToday.has(meditation.id) ? 'Play Again' : 'Play'}
                        </>
                      )}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Ritual Tips */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="glass-heavy">
          <CardContent className="py-8">
            <h3 className="text-xl font-semibold text-white mb-4 text-center">
              Creating Sacred Space
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              <div className="glass-light rounded-xl p-4 text-center">
                <span className="text-2xl mb-2 block">🕯️</span>
                <p className="text-white font-medium mb-1">Set the Mood</p>
                <p className="text-mystic-200/70 text-sm">Light candles, dim lights, play soft music</p>
              </div>
              <div className="glass-light rounded-xl p-4 text-center">
                <span className="text-2xl mb-2 block">🧘‍♀️</span>
                <p className="text-white font-medium mb-1">Clear Your Mind</p>
                <p className="text-mystic-200/70 text-sm">Take deep breaths, release expectations</p>
              </div>
              <div className="glass-light rounded-xl p-4 text-center">
                <span className="text-2xl mb-2 block">💫</span>
                <p className="text-white font-medium mb-1">Set Intentions</p>
                <p className="text-mystic-200/70 text-sm">Focus on what you wish to receive or release</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Rituals;
